import React, { useCallback, useEffect, useReducer } from 'react';
import { FilterGroupEnum, usePrevious, useRenderCount, useGlobalContext, TripType, useSearch, FilterCategoryEnum, } from '@honeycomb/data';
import { Styler, Button, Stacker, Clone, Paper, Skeleton, TypographyBody, Checkbox, } from '@honeycomb/ui-core';
import { pushFilterSelection, pushFilterRemoval, pushFilterDeSelection, pushTextFilterSelection, } from '@honeycomb/tracking';
import { filtersReducer, FilterDispatchActionType } from './FiltersReducer';
import { CheckboxGroupFilter } from './CheckboxGroupFilter';
import { isDifferent, areFiltersClean, mapFiltersToState, calculateLeadingText, areFiltersEmpty } from './utils';
import { TextFilter } from './TextFilter';
import { SliderRangeFilter } from './SliderRangeFilter';
import { BudgetFilter } from './BudgetFilter';
import { isBrowser } from '../../utils/isBrowser';
import { PriceDisplayModeToggle } from '../../components/PriceDisplayModeToggle';
import { useMountEffect } from '../../utils';
/*
    The reasoning for different local and global filter states is that one controls the
        search results that are currently shown (global) whilst the other controls what is
        displayed in the UI (local). This is necessary as changes to the UI by the user
        (selected an option, collapsing an accordion) need to be shown instantly whilst
        their effects on the search results need to be debounced and send as a batch update.
        
    To make this work, a cycle of change/effect is created between the two. When the user
        clicks an option, the local state (and thus UI) is updated instantly, these
        changes are watched and a subsequent debounced action fired to update the global
        state (refreshing the displayed results). If the new results bring back a
        different filter structure to before (could be anything from a whole new group to
        just a different hotel count on a certain filter option) then the local state
        (and thus UI) must be updated accordingly. To stop this cycle becoming an infinite
        loop and to not nuke any user changes that have been made since the last debounced
        call, we only what to update the local state when the global state is structurally
        different. If it's the same structurally or only user defined properties have
        changes then no effect occurs and the cycle is broken.
*/
export function Filters(props) {
    const { data, loading, inboundLeadingText, enabledFilterCategories, filteredRecordCount, onUpdateFilters, onFilterReset, scrollContext = isBrowser() ? window : null, withPriceDisplayModeToggle, } = props;
    const globalFiltersState = mapFiltersToState(data);
    const [hotelSearch, _] = useSearch([]);
    const { tripType } = hotelSearch;
    const { resourceStrings: { RESETFILTERSBUTTON_CAPTION, HC_PRICE_RANGE_FILTER_SUBHEADER }, } = useGlobalContext();
    // !! DEV-Only !! Counts and prints number of renders for component
    useRenderCount('Filters');
    // Set up local state variables for filters
    const [localFiltersState, filterDispatch] = useReducer(filtersReducer, globalFiltersState || []);
    const prevLocalFiltersState = usePrevious(localFiltersState);
    const prevGlobalFiltersState = usePrevious(globalFiltersState);
    const filtering = loading && localFiltersState.length > 0;
    const showSkeleton = loading && !filtering;
    const filtersEmpty = areFiltersEmpty(localFiltersState);
    const radioToggleRef = React.useRef(null);
    const handleCheckboxGroupChange = useCallback((itemId, checked, group, label) => {
        if (checked) {
            pushFilterSelection(group, label);
        }
        else {
            pushFilterDeSelection(group, label);
            pushFilterRemoval(group, label);
        }
        filterDispatch({
            type: FilterDispatchActionType.SET_CHECKBOX_GROUP_ITEM_SELECTED,
            groupIdValue: group,
            checkboxGroupItemIdValue: itemId,
            checkboxGroupItemSelected: checked,
        });
    }, []);
    const handleSliderChange = useCallback((min, max, group) => {
        pushFilterSelection(`${group}`, `${min.toString()}-${max.toString()}`);
        filterDispatch({
            type: FilterDispatchActionType.SET_RANGE_GROUP_VALUES,
            groupIdValue: group,
            rangeGroupLowerValue: min,
            rangeGroupUpperValue: max,
        });
    }, []);
    const handleTextChange = useCallback((group, value) => {
        pushTextFilterSelection(group, value);
        filterDispatch({
            type: FilterDispatchActionType.SET_TEXT_GROUP_VALUE,
            groupIdValue: group,
            groupOpen: true,
            textboxValue: value,
        });
    }, []);
    // When state changes, detect if there is a need to update global (debounced) or local state
    useEffect(() => {
        if (!prevGlobalFiltersState) {
            return;
        }
        if (isDifferent(prevGlobalFiltersState, globalFiltersState)) {
            // Update local state to match global
            filterDispatch({
                type: FilterDispatchActionType.SET_FILTERS,
                filtersValue: globalFiltersState,
            });
            scrollContext === null || scrollContext === void 0 ? void 0 : scrollContext.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
        else if (isDifferent(prevLocalFiltersState, localFiltersState) &&
            isDifferent(globalFiltersState, localFiltersState)) {
            // Local state updated.
            // We no longer debounce this update as debouncing is handled locally for each filter group.
            onUpdateFilters(localFiltersState);
        }
    }, [
        globalFiltersState,
        prevGlobalFiltersState,
        localFiltersState,
        prevLocalFiltersState,
        onUpdateFilters,
        scrollContext,
    ]);
    // When loading the filters inside a dialog, the dialog's scaling transition results in the
    // price mode toggle incorrectly positioning its selection mask. To correct this we force a
    // refresh of the toogle component after a delay.
    useMountEffect(() => {
        setTimeout(() => {
            var _a;
            (_a = radioToggleRef.current) === null || _a === void 0 ? void 0 : _a.refresh();
        }, 300);
    });
    return (React.createElement(React.Fragment, null,
        showSkeleton && (React.createElement(Stacker, { spacing: 5 },
            React.createElement(Skeleton, { radius: 10, maw: "100%" },
                React.createElement(Button, { size: "l", fullWidth: true })),
            React.createElement(Clone, { copies: 6, renderChildren: () => (React.createElement(Paper, { p: 4, pt: 0 },
                    React.createElement(Styler, { py: 4 },
                        React.createElement(TypographyBody, { component: "div", w: 160 },
                            React.createElement(Skeleton, { variant: "text" }))),
                    [80, 120, 70, 90, 110].map((w) => (React.createElement(Stacker, { direction: "row", spacing: 3, alignItems: "center", mih: 48, key: w },
                        React.createElement(Skeleton, { radius: 5, maw: "fit-content", h: "auto" },
                            React.createElement(Checkbox, { size: "s" })),
                        React.createElement(TypographyBody, { component: "div", w: w },
                            React.createElement(Skeleton, { variant: "text" }))))))) }))),
        !showSkeleton && !filtersEmpty && (React.createElement(Stacker, { spacing: 5 },
            withPriceDisplayModeToggle && (React.createElement(PriceDisplayModeToggle, { ref: radioToggleRef, fullWidth: true, withBorder: false })),
            React.createElement(Button, { "data-id": "reset-filter", disabled: areFiltersClean(localFiltersState), fullWidth: true, variant: "outlined", color: "primary", size: "l", onClick: () => {
                    filterDispatch({
                        type: FilterDispatchActionType.RESET_FILTERS,
                    });
                    onFilterReset === null || onFilterReset === void 0 ? void 0 : onFilterReset();
                } }, RESETFILTERSBUTTON_CAPTION),
            localFiltersState.map((filterGroup) => {
                const { type, id, name } = filterGroup; // common filter group properties
                if (!(enabledFilterCategories === null || enabledFilterCategories === void 0 ? void 0 : enabledFilterCategories.includes(id))) {
                    return null;
                }
                // Checkbox group filter
                if (type === FilterGroupEnum.Checkbox) {
                    if (filterGroup.items.length === 0) {
                        return null;
                    }
                    return (React.createElement(CheckboxGroupFilter, { id: id, key: `filter-group-${id}`, items: filterGroup.items, onChange: handleCheckboxGroupChange, heading: name, filteredRecordCount: filteredRecordCount }));
                }
                // Budget filter
                if (type === FilterGroupEnum.Pricerange) {
                    const fg = filterGroup;
                    return (React.createElement(BudgetFilter, { heading: name, subHeading: tripType === TripType.Holiday ? HC_PRICE_RANGE_FILTER_SUBHEADER : '', id: id, min: fg.min, max: fg.max, lower: fg.lower, upper: fg.upper, onChange: handleSliderChange, key: `filter-group-${id}` }));
                }
                // Range filter
                if (type === FilterGroupEnum.Range) {
                    // Slider range slider
                    return (React.createElement(SliderRangeFilter, { id: id, key: `filter-group-${id}`, onChange: handleSliderChange, min: filterGroup.min, max: filterGroup.max, lower: filterGroup.lower, upper: filterGroup.upper, step: filterGroup.step, formatString: filterGroup.formatString || undefined, heading: name, subHeading: id === FilterCategoryEnum.PriceRange && tripType === TripType.Holiday
                            ? HC_PRICE_RANGE_FILTER_SUBHEADER
                            : '', leadingText: calculateLeadingText(filterGroup, localFiltersState, inboundLeadingText) }));
                }
                // Text filter
                if (type === FilterGroupEnum.Text) {
                    return (React.createElement(TextFilter, { id: id, key: `filter-group-${id}`, onChange: (group, value) => {
                            handleTextChange(group, value);
                        }, heading: "Hotel name", value: filterGroup.value || '' }));
                }
                return null;
            })))));
}
